import { UserUpdateInput } from 'dtos/users/UserUpdateInput';
import { makeAutoObservable, runInAction } from 'mobx';
import { AuthStore } from 'stores/users/AuthStore';
import { createForm } from 'forms/editUserForm';
import { Role, emailAlreadyExistsMessage } from 'dtos/users/common';
import { UserListStore } from 'stores/users/UserListStore';
import { Match } from 'navigo';
import { IRouteStore } from 'stores/RouteStore';
import { UserAdminOutput } from 'dtos/users/UserAdminOutput';
import { LoadStatus } from 'utils/types';
import { serverUri } from 'utils/config';
import { nullsToUndefined } from 'utils/nullsToUndefined';

export class EditUserStore {
  form = createForm();
  status: LoadStatus = 'none';
  message?: string;
  editingUser?: UserAdminOutput;

  constructor(
    private readonly authStore: AuthStore,
    private readonly userListStore: UserListStore,
    private readonly routeStore: IRouteStore
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  async activate(match: Partial<Match>) {
    this.status = 'none';
    this.message = undefined;
    this.editingUser = undefined;
    // TODO: avoid loading twice
    if (!this.userListStore.users.length) {
      await this.userListStore.getUsers();
    }
    const username = match?.data?.username;
    if (!username) {
      this.routeStore.navigateByName('login');
      return;
    }
    const foundUser = this.userListStore.users.find((user) => user.username === username);
    if (!foundUser) {
      this.routeStore.navigateByName('login');
      return;
    }
    this.updateFormWithUser(foundUser);
  }

  async updateFormWithUser(user: UserAdminOutput) {
    this.editingUser = user;
    const { email, roles } = this.editingUser;
    this.form.set('default', {
      password: '',
      email,
      enabled: roles.includes(Role.USER),
      internal: roles.includes(Role.INTERNAL),
      admin: roles.includes(Role.ADMIN),
    });
    this.form.reset();
  }

  get canEditRoles() {
    return this.authStore.currentUser?.username !== this.editingUser?.username;
  }

  async editUser() {
    this.status = 'loading';
    this.message = undefined;
    const { isValid } = await this.form.validate({ showErrors: true });
    if (!isValid || !this.editingUser) {
      return;
    }
    const dto = this.formToDto(this.form.values(), this.editingUser);
    try {
      const response = await this.authStore.fetchWithAuth(`${serverUri}/admin/users`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dto),
      });
      if (!response.ok) {
        const json = nullsToUndefined(await response.json());
        runInAction(() => {
          this.status = 'error';
          if (json && json.message === emailAlreadyExistsMessage) {
            this.message = 'Email address is already in use';
          } else {
            this.message = `Unable to update user.${json.message ? ` ${json.message}` : ''}`;
          }
        });
        return;
      }
      runInAction(() => {
        this.status = 'success';
        this.message = 'The user was updated.';
      });
    } catch (err) {
      runInAction(() => {
        this.status = 'error';
        this.message = `Unable to update user.${err instanceof Error ? ` ${err.message}` : ''}`;
      });
    }
  }

  private formToDto(form: any, user: UserAdminOutput): UserUpdateInput {
    const { username } = user;
    const { password, email, enabled, internal, admin } = form;
    const editedRoles: Role[] = [];
    if (enabled) editedRoles.push(Role.USER);
    if (internal) editedRoles.push(Role.INTERNAL);
    if (admin) editedRoles.push(Role.ADMIN);
    const roles = this.canEditRoles ? editedRoles : this.authStore.currentUser?.roles || [];
    const dto: UserUpdateInput = {
      username,
      password,
      email,
      roles,
    };
    return dto;
  }
}
